// OAuth consent screen — the user-facing half of Supabase's OAuth 2.1 server
// (Atlas MCP Phase 3).
//
// When an MCP client (claude.ai, Claude Desktop, Claude Code) starts an OAuth
// flow against our Supabase project, Supabase authenticates the user and then
// redirects the browser to the project's Site URL with ?authorization_id=…
// for the app to render consent. app.jsx spots that query param at boot and
// renders this component (inside AuthGate, so the user is signed in) instead
// of the app shell.
//
// The two consent calls are plain GoTrue REST (the SPA has no supabase-js):
//   GET  /auth/v1/oauth/authorizations/{id}          → client, user, scope
//   POST /auth/v1/oauth/authorizations/{id}/consent  {action} → redirect_url
// Both require the signed-in user's access token AND must be called from the
// project's Site URL origin — GoTrue checks the Origin header, which is why
// this page can't live on the API host.

const { useState, useEffect } = React;

const CONSENT_SUPABASE_URL = (typeof window !== "undefined" && window.__SUPABASE_URL__) || "";
const CONSENT_ANON_KEY = (typeof window !== "undefined" && window.__SUPABASE_ANON_KEY__) || "";

// Human wording per OAuth scope. Unknown scopes render verbatim rather than
// disappearing — never hide what the client asked for.
const SCOPE_DESCRIPTIONS = {
  email: "See your name and email address",
  profile: "See your basic profile",
  openid: "Confirm your identity",
  offline_access: "Stay connected without asking again",
  phone: "See your phone number",
};

const consentHeaders = (accessToken) => ({
  apikey: CONSENT_ANON_KEY,
  Authorization: `Bearer ${accessToken}`,
  "Content-Type": "application/json",
});

const OAuthConsent = ({ authorizationId }) => {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!CONSENT_SUPABASE_URL) throw new Error("Auth is not configured in this environment.");
        const session = await window.AtlasAuth?.getValidSession?.(60);
        if (!session?.access_token) throw new Error("You need to sign in to Atlas first.");
        const r = await fetch(
          `${CONSENT_SUPABASE_URL}/auth/v1/oauth/authorizations/${encodeURIComponent(authorizationId)}`,
          { headers: consentHeaders(session.access_token) },
        );
        if (!r.ok) {
          // Authorizations expire after ~10 minutes; a dead id is the common
          // failure, so say that rather than parroting a status code.
          throw new Error(
            r.status === 404
              ? "This authorization request has expired or was already handled. Go back to the app that sent you here and try connecting again."
              : `Couldn't load the authorization request (HTTP ${r.status}).`,
          );
        }
        const d = await r.json();
        if (!cancelled) setDetails(d);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [authorizationId]);

  const decide = async (action) => {
    setBusy(true);
    setError(null);
    try {
      const session = await window.AtlasAuth?.getValidSession?.(60);
      if (!session?.access_token) throw new Error("Your session expired — reload and sign in again.");
      const r = await fetch(
        `${CONSENT_SUPABASE_URL}/auth/v1/oauth/authorizations/${encodeURIComponent(authorizationId)}/consent`,
        {
          method: "POST",
          headers: consentHeaders(session.access_token),
          body: JSON.stringify({ action }),
        },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.msg || d.message || `Consent failed (HTTP ${r.status}).`);
      if (!d.redirect_url) throw new Error("Supabase didn't return a redirect URL.");
      setRedirecting(true);
      window.location.assign(d.redirect_url);
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  const scopes = (details?.scope || "").split(" ").filter(Boolean);
  const clientName = details?.client?.name || "An application";
  const clientUri = details?.client?.uri || "";

  const btn = (primary) => ({
    flex: 1,
    padding: "10px 0",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: busy ? "default" : "pointer",
    border: primary ? "1px solid transparent" : "1px solid var(--border, #d1d5db)",
    background: primary ? "var(--accent, #111)" : "var(--surface, #fff)",
    color: primary ? "var(--accent-fg, #fff)" : "var(--text, #111)",
    opacity: busy ? 0.6 : 1,
  });

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        background: "var(--bg, #fafbfc)",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      <div
        style={{
          background: "var(--surface, #fff)",
          border: "1px solid var(--border, #e5e7eb)",
          borderRadius: 12,
          padding: "32px 36px",
          width: 440,
          maxWidth: "calc(100vw - 32px)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
          margin: "auto 16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 22 }}>
          <img src="/android-chrome-512x512.png" alt="IndustryPro" width={42} height={42}
            style={{ display: "block", flexShrink: 0 }} />
          <span
            style={{
              fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
              fontStyle: "italic",
              fontWeight: 600,
              fontSize: 33,
              lineHeight: 1,
              color: "var(--text, #111)",
              letterSpacing: "0.005em",
            }}
          >
            Atlas
          </span>
        </div>

        {error ? (
          <>
            <div style={{ fontSize: 14, color: "var(--danger, #b91c1c)", lineHeight: 1.5 }}>{error}</div>
            <div style={{ marginTop: 18 }}>
              <a href={window.location.pathname} style={{ fontSize: 13, color: "var(--text-2, #666)" }}>
                Open Atlas
              </a>
            </div>
          </>
        ) : !details ? (
          <div style={{ fontSize: 14, color: "var(--text-2, #666)" }}>Loading authorization request…</div>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text, #111)", lineHeight: 1.4 }}>
              {clientName} wants to connect to Atlas
            </div>
            {clientUri && (
              <div style={{ fontSize: 12, color: "var(--text-3, #888)", marginTop: 4, wordBreak: "break-all" }}>
                {clientUri}
              </div>
            )}

            <div style={{ fontSize: 13, color: "var(--text-2, #666)", marginTop: 18, marginBottom: 8 }}>
              It will be able to:
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: "var(--text, #111)", lineHeight: 1.9 }}>
              {(scopes.length ? scopes : ["email"]).map((s) => (
                <li key={s}>{SCOPE_DESCRIPTIONS[s] || s}</li>
              ))}
              <li>Read CRM data through the Atlas MCP tools, as you</li>
            </ul>

            <div style={{ fontSize: 12, color: "var(--text-3, #888)", marginTop: 18 }}>
              Signed in as <strong style={{ color: "var(--text-2, #666)" }}>{details?.user?.email || "you"}</strong>.
              You can revoke this later from your Supabase account grants.
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button style={btn(false)} disabled={busy} onClick={() => decide("deny")}>
                Deny
              </button>
              <button style={btn(true)} disabled={busy} onClick={() => decide("approve")}>
                {redirecting ? "Returning…" : "Approve"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

window.OAuthConsent = OAuthConsent;
